import { FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';

import {
  BatchApiStatusHandler,
  Button,
  Error,
  Section,
  UsStateSelectField,
  FieldError,
} from '../components';
import { fetchApi } from '../utils/fetchApi';
import { ApiStatus, IApiData } from '../utils/types';
import { LegalEntityRegion, RegionStatus } from '../types';
import { useBatchApi } from '../hooks';
import { useGlobalContext } from '../contexts/globalContext';

type UsSetupProps = {
  legalEntityRegions: LegalEntityRegion[];
};

const UsSetup: FunctionComponent<UsSetupProps> = ({ legalEntityRegions }) => {
  const { setGlobalState } = useGlobalContext();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);
  const [isNextLoading, setIsNextLoading] = useState<boolean>(false);

  const formMethods = useForm();
  const {
    handleSubmit,
    clearErrors,
    formState: { errors },
  } = formMethods;

  const legalEntityRegionCodes: string[] = legalEntityRegions.map((ler) => {
    return ler.region.code;
  });

  const onSubmit = (data: any) => {
    setIsNextLoading(true);
    clearErrors();
    setFormError(null);
    // Make an API call to create LegalEntityRegion and related requirements
    fetchApi({
      url: `/api/legal_entity/locations/${data.state}/requirements`,
      method: 'POST',
      body: { status: RegionStatus.Maintaining },
    }).then(({ status, data: responseData }: IApiData) => {
      if (status === ApiStatus.Error) {
        setFormError("Something went wrong, we're looking into it.");
        setIsNextLoading(false);
        return;
      }

      setGlobalState((oldState) => {
        const newState = { ...oldState };
        newState.user.legal_entity.regions = [
          ...newState.user.legal_entity.regions,
          responseData,
        ];

        return newState;
      });

      navigate(`/locations/usa/${data.state}`);
    });
  };

  return (
    <Section className="h-full">
      <div className="border-b pb-4">
        <h1 className="text-2xl font-bold">Add a new location</h1>
      </div>

      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="max-w-sm py-8">
            <fieldset className="pb-8">
              {errors?.state && (
                <Error heading="Please select a state" body="" />
              )}
              <div className="my-2 py-2">
                <UsStateSelectField
                  name="state"
                  label="Which location would you like to set up?"
                  description=""
                  reactFormConfig={{ required: 'This is required' }}
                  error={errors.hq_state}
                  disabledStateOptions={legalEntityRegionCodes}
                />
              </div>
            </fieldset>
            <Button type="submit" size="large" disabled={isNextLoading}>
              Next
            </Button>
            {formError && (
              <FieldError error={{ type: 'manual', message: formError }} />
            )}
          </div>
        </form>
      </FormProvider>
    </Section>
  );
};

export const UsSetupView: FunctionComponent = () => {
  const batchResponse = useBatchApi([
    {
      url: '/api/legal_entity/locations',
      method: 'GET',
    },
  ]);

  const componentPropsFn = ([legalEntityRegions]: any[]): UsSetupProps => {
    return {
      legalEntityRegions,
    };
  };

  return (
    <BatchApiStatusHandler
      batchResponse={batchResponse}
      component={UsSetup}
      componentProps={componentPropsFn}
    />
  );
};

export default UsSetupView;
